import { Formik, FormikHelpers } from 'formik';
import React, { useRef } from 'react';
import { useApiRequest } from '../../../api/useApiRequest';
import { AssertUserHasPermission } from '../../../authorisation/AssertUserHasPermission';
import { assertNotNull } from '../../../helpers/nullHelpers';
import { RouteComponentProps } from '../../../helpers/RouteComponentProps';
import { urls } from '../../../helpers/urls';
import { BreadcrumbBar, BreadcrumbLink, BreadcrumbSeparator } from '../../../layout/Breadcrumbs';
import { PageContainer } from '../../../layout/Layout';
import { FormErrorBox } from '../../../shared/form/Form';
import { Header1 } from '../../../shared/Headers';
import { Project, ProjectHeader } from '../../projects/project-section/ProjectHeader';
import { projectUrls } from '../../projects/projectUrls';
import { QuoteResponse } from '../../quotes/quoteResponse';
import { GetDataForQuoteOrDesignForm } from '../../shared/quote-or-design-form/GetDataForQuoteOrDesignForm';
import { CreateDesignResponse } from '../createDesignResponse';
import { CreateDesignCommand, mapDesignFormModelToCreateCommand } from '../createUpdateDesignDto';
import { DesignForm } from '../design-form/DesignForm';
import { NextStepAfterSavingDesign } from '../design-form/DesignFormButtons';
import { navigateAfterSavingDesign } from '../design-form/navigateAfterSavingDesign';
import { DesignFormModel, designFormModelValidator, getInitialDesignFormModel } from '../design-form/designFormModel';
import { GetRequest } from '../../../shared/GetRequest';

type Props = { project: Project } & RouteComponentProps<{ quoteId: string }>;

export const CreateDesign = (props: Props) => {
  const getQuoteRequest = useApiRequest<QuoteResponse>({
    endpointUrl: `projects/${props.project.projectId}/quotes/${props.quoteId}`,
    httpMethod: 'get',
  });

  const createDesignRequest = useApiRequest<CreateDesignResponse, CreateDesignCommand>({
    httpMethod: 'post',
    endpointUrl: `projects/${props.project.projectId}/designs`,
  });

  const nextStepAfterSave = useRef<NextStepAfterSavingDesign>('BackToProject');

  return (
    <GetRequest getDataRequest={getQuoteRequest}>
      {({ response: quoteResponse }) => {
        const onSubmit = (formModel: DesignFormModel, formikHelpers: FormikHelpers<DesignFormModel>) => {
          const createDesignCommand = mapDesignFormModelToCreateCommand(
            formModel,
            props.project.projectId,
            Number(assertNotNull(props.quoteId)),
            quoteResponse.pipes,
          );

          if (createDesignRequest.inProgress) {
            return;
          }

          return createDesignRequest.sendRequest({
            requestBody: createDesignCommand,
            onSuccess: response => {
              formikHelpers.setSubmitting(false);

              return navigateAfterSavingDesign(
                props.project.projectId,
                response.designId,
                createDesignCommand.design.isDraft,
                nextStepAfterSave.current,
              );
            },
            onFailure: () => {
              formikHelpers.setSubmitting(false);
            },
          });
        };

        return (
          <>
            <BreadcrumbBar>
              <BreadcrumbLink to={urls.home}>Projects</BreadcrumbLink>
              <BreadcrumbSeparator />
              <BreadcrumbLink to={projectUrls.projectDetails(props.project.projectId)}>
                {props.project.projectReference}
              </BreadcrumbLink>
              <BreadcrumbSeparator />
              <div>New Design</div>
            </BreadcrumbBar>
            <ProjectHeader project={props.project} />
            <PageContainer>
              <AssertUserHasPermission requiredPermission="Edit">
                <GetDataForQuoteOrDesignForm project={props.project}>
                  {dataForQuoteOrDesignForm => (
                    <>
                      <Header1>New Design</Header1>
                      {createDesignRequest.error && <FormErrorBox error={createDesignRequest.error} />}
                      <Formik<DesignFormModel>
                        onSubmit={onSubmit}
                        initialValues={getInitialDesignFormModel(
                          quoteResponse,
                          dataForQuoteOrDesignForm,
                          props.project.buildTypeCode,
                        )}
                        validate={formModel => designFormModelValidator.validate(formModel)}
                      >
                        {formProps => (
                          <DesignForm
                            {...formProps}
                            project={props.project}
                            dataForQuoteOrDesignForm={dataForQuoteOrDesignForm}
                            setNextStepAfterSave={nextStep => {
                              nextStepAfterSave.current = nextStep;
                            }}
                          />
                        )}
                      </Formik>
                      {createDesignRequest.error && <FormErrorBox error={createDesignRequest.error} />}
                    </>
                  )}
                </GetDataForQuoteOrDesignForm>
              </AssertUserHasPermission>
            </PageContainer>
          </>
        );
      }}
    </GetRequest>
  );
};
